import {
  type ConversationProviderMetadata,
  ConversationStatus,
  type IBrand,
  type IPatient,
  type IPractice,
  type SMSConversation,
  type SMSMessage,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  Firestore,
  type IReffable,
  type Timestamp,
  toTimestamp,
  type Transaction,
  undeletedQuery,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { Brand } from '../brand';
import { InboundMessagePatient } from './inbound-message-patient';

export interface IInboundConversationProvider {
  practicePhoneNumber: string;
  metadata: ConversationProviderMetadata;
}

/**
 * An inbound reply joins the practice's existing conversation where there is
 * one, so reception reads it in context. How that conversation is found
 * depends on what the caller knows: just the sender's number, or the patient.
 */
export class InboundMessageConversation {
  /**
   * For provider webhooks, which only know the sender's number. The
   * practice's conversation with that number decides the patient, so a reply
   * on a shared family mobile joins it. `undefined` when no patient the
   * practice receives messages from has the number.
   */
  static async findOrCreate(
    brand: IReffable<IBrand>,
    practiceRef: DocumentReference<IPractice>,
    patientPhoneNumber: string,
    provider: IInboundConversationProvider
  ): Promise<WithRef<SMSConversation> | undefined> {
    const matchingPatient = await InboundMessagePatient.findBestMatch(
      brand,
      patientPhoneNumber
    );
    if (!matchingPatient) {
      return;
    }

    const existingConversation = await Firestore.firstResult(
      undeletedQuery(Brand.smsConversationCol(brand)),
      where('patientPhoneNumber', '==', patientPhoneNumber),
      where('practiceRef', '==', practiceRef)
    );
    if (existingConversation) {
      return InboundMessageConversation._openIfPending(
        existingConversation,
        provider
      );
    }

    return InboundMessageConversation._create(
      brand,
      matchingPatient.ref,
      practiceRef,
      patientPhoneNumber,
      provider
    );
  }

  /**
   * For callers that already chose the patient, such as the API. Only that
   * patient's own conversation is joined, never one belonging to another
   * patient on the same number.
   */
  static async findOrCreateForPatient(
    brand: IReffable<IBrand>,
    patientRef: DocumentReference<IPatient>,
    practiceRef: DocumentReference<IPractice>,
    patientPhoneNumber: string,
    provider: IInboundConversationProvider
  ): Promise<WithRef<SMSConversation>> {
    const existingConversation = await Firestore.firstResult(
      undeletedQuery(Brand.smsConversationCol(brand)),
      where('patientRef', '==', patientRef),
      where('practiceRef', '==', practiceRef)
    );
    if (existingConversation) {
      return InboundMessageConversation._openIfPending(
        existingConversation,
        provider
      );
    }

    return InboundMessageConversation._create(
      brand,
      patientRef,
      practiceRef,
      patientPhoneNumber,
      provider
    );
  }

  static async addReply(
    conversation: WithRef<SMSConversation>,
    messageRef: DocumentReference<SMSMessage>,
    receivedAt: Timestamp = toTimestamp(),
    transaction?: Transaction
  ): Promise<void> {
    const reopen: Partial<SMSConversation> =
      conversation.status === ConversationStatus.Closed
        ? { label: '', status: ConversationStatus.Open }
        : {};

    await Firestore.patchDoc(
      conversation.ref,
      {
        ...reopen,
        lastMessageAt: receivedAt,
        lastMessage: messageRef,
      },
      transaction
    );
  }

  /**
   * A conversation a staff member started but hasn't sent from is still
   * pending, without provider metadata. A reply opens it, as sending would.
   */
  private static async _openIfPending(
    conversation: WithRef<SMSConversation>,
    provider: IInboundConversationProvider
  ): Promise<WithRef<SMSConversation>> {
    if (conversation.status !== ConversationStatus.Pending) {
      return conversation;
    }

    const opened = {
      status: ConversationStatus.Open,
      metadata: provider.metadata,
    };
    await Firestore.patchDoc(conversation.ref, opened);
    return { ...conversation, ...opened };
  }

  private static async _create(
    brand: IReffable<IBrand>,
    patientRef: DocumentReference<IPatient>,
    practiceRef: DocumentReference<IPractice>,
    patientPhoneNumber: string,
    provider: IInboundConversationProvider
  ): Promise<WithRef<SMSConversation>> {
    const conversationRef = await Firestore.addDoc(
      Brand.smsConversationCol(brand),
      {
        patientPhoneNumber,
        practicePhoneNumber: provider.practicePhoneNumber,
        patientRef,
        practiceRef,
        status: ConversationStatus.Open,
        lastMessageAt: toTimestamp(),
        metadata: provider.metadata,
        deleted: false,
      }
    );
    return Firestore.getDoc(conversationRef);
  }
}
